import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import TarotCard from "@/components/TarotCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, RotateCcw, Eye } from "lucide-react";
import eligeTresCartasAudio from "@/assets/elige-tres-cartas.mp3";
import tarotMago from "@/assets/tarot-mago.png";
import tarotSacerdotisa from "@/assets/tarot-sacerdotisa.png";
import tarotEmperatriz from "@/assets/tarot-emperatriz.png";
import tarotEmperador from "@/assets/tarot-emperador.png";
import tarotSumoSacerdote from "@/assets/tarot-sumo-sacerdote.png";
import tarotEnamorados from "@/assets/tarot-enamorados.png";
import tarotCarro from "@/assets/tarot-carro.png";
import tarotFuerza from "@/assets/tarot-fuerza.png";
import tarotErmitano from "@/assets/tarot-ermitano.png";
import tarotRuedaFortuna from "@/assets/tarot-rueda-fortuna.png";
import tarotGuide from "@/assets/tarot-guide.jpg";
import { createWebApplicationSchema } from "@/utils/schemaHelpers";

const tarotCards = [
  {
    name: "El Mago",
    image: tarotMago,
    position: "Pasado",
    meaning: "Representa el poder de manifestar los sueños en la realidad. Es la conexión entre el mundo espiritual y el material. Invita a usar creatividad, habilidades y recursos para transformar tu realidad."
  },
  {
    name: "La Sacerdotisa",
    image: tarotSacerdotisa,
    position: "Presente",
    meaning: "Simboliza la intuición, el misterio y el conocimiento oculto. Es la guardiana de los secretos del alma. Aconseja escuchar la voz interior antes de actuar. Confía en tu sabiduría interna."
  },
  {
    name: "La Emperatriz",
    image: tarotEmperatriz,
    position: "Futuro",
    meaning: "Encarna la abundancia, la fertilidad y el amor maternal. Representa la belleza, la creatividad y la conexión con la naturaleza. Un augurio de crecimiento y prosperidad en todos los aspectos de tu vida."
  },
  {
    name: "El Emperador",
    image: tarotEmperador,
    position: "Pasado",
    meaning: "Simboliza la autoridad, la estructura y el liderazgo. Representa la estabilidad y la razón. Señala control, poder y orden interior."
  },
  {
    name: "El Sumo Sacerdote",
    image: tarotSumoSacerdote,
    position: "Presente",
    meaning: "Encierra la sabiduría ancestral y la guía espiritual. Representa las tradiciones y la enseñanza. Inspira a buscar consejo y a seguir la fe."
  },
  {
    name: "Los Enamorados",
    image: tarotEnamorados,
    position: "Futuro",
    meaning: "Carta del amor, la unión y las decisiones del corazón. Representa la dualidad y la elección consciente. Invita a la armonía y al compromiso."
  },
  {
    name: "El Carro",
    image: tarotCarro,
    position: "Pasado",
    meaning: "Simboliza el triunfo por medio de la determinación. Representa el avance, la dirección y el dominio de las emociones. Señala éxito tras el esfuerzo."
  },
  {
    name: "La Fuerza",
    image: tarotFuerza,
    position: "Presente",
    meaning: "Es el poder de la calma, la compasión y el valor interior. Dominar sin violencia, guiar con ternura. Indica autocontrol y fortaleza emocional."
  },
  {
    name: "El Ermitaño",
    image: tarotErmitano,
    position: "Futuro",
    meaning: "Refleja la búsqueda interior y la sabiduría solitaria. Representa el aprendizaje a través de la introspección. Invita al silencio y la reflexión."
  },
  {
    name: "La Rueda de la Fortuna",
    image: tarotRuedaFortuna,
    position: "Presente",
    meaning: "Simboliza el cambio, el destino y los ciclos de la vida. Representa oportunidades que giran a tu favor. Aconseja aceptar el movimiento constante del universo."
  }
];

const Tarot = () => {
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [revealedCards, setRevealedCards] = useState<typeof tarotCards>([]);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const appSchema = createWebApplicationSchema(
    "Lectura de Tarot Gratis - 3 Cartas",
    "Tirada de tarot gratis de 3 cartas para descubrir tu pasado, presente y futuro en amor y destino.",
    "EntertainmentApplication"
  );

  useEffect(() => {
    if (!revealed && !audioRef.current) {
      audioRef.current = new Audio(eligeTresCartasAudio);
      audioRef.current.play().catch(error => {
        if (import.meta.env.DEV) {
          console.log("Audio autoplay prevented:", error);
        }
      });
    }
  }, [revealed]);

  const selectCard = (index: number) => {
    if (selectedCards.length < 3 && !selectedCards.includes(index)) {
      setSelectedCards([...selectedCards, index]);
    } else if (selectedCards.includes(index)) {
      setSelectedCards(selectedCards.filter(i => i !== index));
    }
  };

  const revealReading = () => {
    if (selectedCards.length === 3) {
      setIsRevealing(true);
      
      // Shuffle and select 3 random cards
      const shuffled = [...tarotCards].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 3);
      
      // Assign positions
      selected[0].position = "Pasado";
      selected[1].position = "Presente";
      selected[2].position = "Futuro";
      
      setRevealedCards(selected);
      
      // Delay the reveal for dramatic effect
      setTimeout(() => {
        setRevealed(true);
        setIsRevealing(false);
        // Show interpretation after cards are flipped
        setTimeout(() => {
          setShowInterpretation(true);
        }, 2000);
      }, 500);
    }
  };

  const resetReading = () => {
    setSelectedCards([]);
    setRevealed(false);
    setIsRevealing(false);
    setRevealedCards([]);
    setShowInterpretation(false);
    audioRef.current = null;
  };

  return (
    <>
      <Helmet>
        <title>Lectura de Tarot Gratis - Tirada de 3 Cartas | Dstino</title>
        <meta name="description" content="Lectura de tarot gratis online. Tirada de 3 cartas para descubrir tu pasado, presente y futuro en amor y destino. Consulta el tarot ahora." />
        <meta property="og:title" content="Lectura de Tarot Gratis - 3 Cartas" />
        <meta property="og:description" content="Tirada de tarot gratis para amor y destino. Descubre tu pasado, presente y futuro." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/tarot`} />
      </Helmet>

      <SchemaMarkup schema={appSchema} />

      <div className="min-h-screen bg-background overflow-hidden">
        <Navigation />
        
        {/* Mystical background effects */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-3xl" />
        </div>
        
        <main className="relative pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Tarot" }
              ]}
            />

            {/* Hero Section */}
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-secondary/5 to-transparent rounded-3xl blur-3xl"></div>
              <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 py-8">
                <div className="relative group animate-mystical-float">
                  <div className="absolute -inset-2 bg-gradient-to-r from-primary/40 via-secondary/40 to-primary/40 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  <div className="relative">
                    <img 
                      src={tarotGuide} 
                      alt="Guía espiritual de Tarot" 
                      className="w-48 h-48 md:w-64 md:h-64 rounded-2xl object-cover shadow-2xl border-2 border-primary/30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent rounded-2xl"></div>
                  </div>
                </div>
                <div className="text-center md:text-left max-w-2xl">
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 text-glow">
                    Lectura de Tarot Gratis
                  </h1>
                  <p className="text-lg md:text-xl text-muted-foreground mb-4">
                    La Tirada de las 3 Cartas para el Amor y el Destino
                  </p>
                  <p className="text-sm md:text-base text-muted-foreground/80 italic">
                    "Deja que las cartas revelen los secretos de tu camino. Confía en la energía que te guía."
                  </p>
                </div>
              </div>
            </div>

            {!revealed ? (
              <Card className="p-6 md:p-8 gradient-celestial mb-8 border-primary/20 animate-glow-pulse">
                <div className="text-center mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3 text-glow">
                    ✨ Elige 3 Cartas ✨
                  </h2>
                  <p className="text-muted-foreground max-w-xl mx-auto">
                    Concéntrate en tu pregunta. Deja que tu intuición te guíe hacia las cartas que resonan contigo.
                  </p>
                </div>

                {/* Card Selection Grid */}
                <div className="grid grid-cols-5 md:grid-cols-10 gap-2 md:gap-3 mb-8 max-w-4xl mx-auto">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div
                      key={index}
                      className="animate-card-spread"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <TarotCard
                        index={index}
                        isSelected={selectedCards.includes(index)}
                        isRevealed={false}
                        onSelect={selectCard}
                        disabled={selectedCards.length >= 3 && !selectedCards.includes(index)}
                      />
                    </div>
                  ))}
                </div>

                {/* Selection Status */}
                <div className="text-center space-y-4">
                  <div className="flex justify-center gap-3">
                    {[0, 1, 2].map((slot) => (
                      <div
                        key={slot}
                        className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                          selectedCards[slot] !== undefined
                            ? "border-primary bg-primary/20 scale-110"
                            : "border-border/50 bg-card/30"
                        }`}
                      >
                        {selectedCards[slot] !== undefined ? (
                          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                        ) : (
                          <span className="text-muted-foreground text-sm">{slot + 1}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-lg font-medium">
                    <span className="text-primary">{selectedCards.length}</span>
                    <span className="text-muted-foreground"> / 3 cartas seleccionadas</span>
                  </p>
                  
                  <Button
                    onClick={revealReading}
                    disabled={selectedCards.length !== 3 || isRevealing}
                    size="lg"
                    className="px-8 py-6 text-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {isRevealing ? (
                        <>
                          <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                          Revelando...
                        </>
                      ) : (
                        <>
                          <Eye className="w-5 h-5" />
                          Revelar Mi Lectura
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-yellow-500 to-primary bg-[length:200%_100%] animate-shimmer opacity-0 group-hover:opacity-30 transition-opacity" />
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="space-y-8">
                {/* Revealed Cards */}
                <Card className="p-6 md:p-8 gradient-mystical border-primary/20">
                  <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-glow">
                    🔮 Tu Lectura del Tarot 🔮
                  </h2>

                  <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-8">
                    {revealedCards.map((card, idx) => (
                      <div key={idx} className="space-y-4">
                        {/* Position Label */}
                        <div className="text-center">
                          <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                            idx === 0 ? "bg-blue-500/20 text-blue-400" :
                            idx === 1 ? "bg-yellow-500/20 text-yellow-400" :
                            "bg-purple-500/20 text-purple-400"
                          }`}>
                            {card.position}
                          </span>
                        </div>
                        
                        {/* Card with flip animation */}
                        <div className="max-w-[200px] mx-auto">
                          <TarotCard
                            index={idx}
                            isSelected={false}
                            isRevealed={true}
                            revealedCard={card}
                            onSelect={() => {}}
                            disabled={true}
                            revealDelay={idx * 500}
                          />
                        </div>
                        
                        {/* Card Meaning */}
                        <div 
                          className={`bg-card/50 backdrop-blur rounded-lg p-4 border border-border/30 transition-all duration-700 ${
                            showInterpretation ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                          }`}
                          style={{ transitionDelay: `${idx * 200}ms` }}
                        >
                          <h3 className="font-bold text-lg mb-2 text-primary">
                            {card.name}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {card.meaning}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* General Interpretation */}
                  <div 
                    className={`bg-card/50 backdrop-blur rounded-lg p-6 border border-primary/20 transition-all duration-700 ${
                      showInterpretation ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    }`}
                    style={{ transitionDelay: "600ms" }}
                  >
                    <h3 className="text-xl md:text-2xl font-bold mb-4 text-center text-glow">
                      ✨ Interpretación General ✨
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-center max-w-3xl mx-auto">
                      Las cartas revelan un momento de transformación en tu vida. 
                      El pasado te ha preparado para los desafíos actuales, y el futuro 
                      promete nuevas oportunidades si mantienes una actitud positiva y 
                      abierta al cambio. Confía en tu intuición y sigue adelante con 
                      valentía. Los astros están alineados a tu favor.
                    </p>
                  </div>

                  <div className="text-center mt-8">
                    <Button 
                      onClick={resetReading} 
                      variant="outline" 
                      size="lg"
                      className="px-8 group"
                    >
                      <RotateCcw className="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                      Nueva Lectura
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Information Section */}
            <Card className="p-6 md:p-8 gradient-celestial mt-8 border-border/30">
              <h2 className="text-xl md:text-2xl font-bold mb-4">
                Sobre la Tirada de 3 Cartas
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  La tirada de tres cartas es una de las lecturas más populares del tarot. 
                  Cada carta representa un aspecto diferente de tu situación:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-foreground">Pasado:</strong> Los acontecimientos y energías que te han llevado al momento presente</li>
                  <li><strong className="text-foreground">Presente:</strong> Tu situación actual y los desafíos o oportunidades que enfrentas ahora</li>
                  <li><strong className="text-foreground">Futuro:</strong> El resultado probable si continúas en tu camino actual</li>
                </ul>
                <p>
                  Recuerda que el tarot es una herramienta de autoconocimiento. Las cartas 
                  te ayudan a reflexionar sobre tu vida y tus decisiones, pero tú siempre 
                  tienes el poder de crear tu propio destino.
                </p>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </>
  );
};

export default Tarot;
